import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWeb3 } from '../../hooks/useWeb3';
import { useProjects } from '../../hooks/useProjects';
import ProjectCard from '../../components/project/ProjectCard';
import DashboardHeader from '../../components/common/DashboardHeader';
import Toast from '../../components/common/Toast';

type TabType = 'my-projects' | 'create-project';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { account, connect, disconnect, loading: web3Loading } = useWeb3();
  const { loading: projectsLoading, fetchProjects, fetchDemoProjects } = useProjects();
  const [activeTab, setActiveTab] = useState<TabType>('my-projects');
  const [filter, setFilter] = useState<'all' | 'active' | 'completed' | 'requires-action'>('all');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [demoProjects, setDemoProjects] = useState<any[]>([]);
  const [userProjects, setUserProjects] = useState<any[]>([]);

  useEffect(() => {
    let isMounted = true;

    // Load demo projects for "My Projects" tab (always show them)
    fetchDemoProjects().then((demos) => {
      if (isMounted) {
        setDemoProjects(demos);
      }
    }).catch(() => {
      if (isMounted) {
        setDemoProjects([]);
      }
    });

    // Load user projects if wallet is connected
    if (account) {
      fetchProjects(account).then((user) => {
        if (isMounted) {
          setUserProjects(user);
        }
      }).catch(() => {
        if (isMounted) {
          setUserProjects([]);
        }
      });
    } else {
      setUserProjects([]);
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  // Determine which projects to show based on active tab
  const displayProjects = activeTab === 'my-projects' 
    ? [...demoProjects, ...userProjects]
    : [];

  // Helper function to check if project requires action
  const projectRequiresAction = (project: any) => {
    if (!account || project.status === 'completed') return false;
    const isClient = project.client.toLowerCase() === account.toLowerCase();
    const isProvider = project.provider.toLowerCase() === account.toLowerCase();
    if (!isClient && !isProvider) return false;
    
    return project.milestones.some((m: any) => {
      if (isClient && !m.approvedByClient) return true;
      if (isProvider && !m.approvedByProvider) return true;
      if (m.approvedByClient && m.approvedByProvider && !m.released) return true;
      return false;
    });
  };

  const filteredProjects = displayProjects.filter((project) => {
    if (filter === 'all') return true;
    if (filter === 'requires-action') return projectRequiresAction(project);
    return project.status === filter;
  });

  const handleConnectWallet = async () => {
    try {
      await connect();
      setToast({ message: 'Wallet connected successfully', type: 'success' });
      // Reload user projects after connection
      if (account) {
        fetchProjects(account).then((user) => {
          setUserProjects(user);
        });
      }
    } catch (error: any) {
      console.error('Failed to connect:', error);
      setToast({ message: error.message || 'Failed to connect wallet', type: 'error' });
    }
  };

  const handleDisconnectWallet = async () => {
    await disconnect();
    setToast({ message: 'Wallet disconnected', type: 'success' });
    setUserProjects([]);
  };

  const handleCreateProject = () => {
    if (!account) {
      setToast({ message: 'Please connect your wallet first', type: 'error' });
      return;
    }
    navigate('/dashboard/projects/new');
  };

  return (
    <div className="min-h-screen bg-white">
      <DashboardHeader
        onConnect={handleConnectWallet}
        onDisconnect={handleDisconnectWallet}
      />

      <div className="w-full max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Internal Navigation Tabs */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-semibold text-[#471b00] font-poly mb-2">
                Dashboard
              </h1>
              <p className="text-sm text-[#471b007f] font-dm">
                {activeTab === 'my-projects' 
                  ? "Manage your projects and explore examples"
                  : "Create a new project"}
              </p>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-2 mb-6 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('my-projects')}
              className={`px-6 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'my-projects'
                  ? 'border-[#FF3333] text-[#FF3333]'
                  : 'border-transparent text-gray-600 hover:text-[#471b00]'
              }`}
            >
              My Projects
            </button>
            <button
              onClick={() => {
                setActiveTab('create-project');
                handleCreateProject();
              }}
              className={`px-6 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'create-project'
                  ? 'border-[#FF3333] text-[#FF3333]'
                  : 'border-transparent text-gray-600 hover:text-[#471b00]'
              }`}
            >
              Create Project
            </button>
          </div>

          {/* Filter buttons - only show in My Projects tab */}
          {activeTab === 'my-projects' && (
            <div className="flex gap-2 mb-6 flex-wrap">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm ${
                  filter === 'all'
                    ? 'bg-gradient-to-r from-[#FF3333] to-[#FF5555] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              {account && (
                <button
                  onClick={() => setFilter('requires-action')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm relative ${
                    filter === 'requires-action'
                      ? 'bg-gradient-to-r from-[#FF3333] to-[#FF5555] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Requires Action
                  {displayProjects.filter(projectRequiresAction).length > 0 && (
                    <span className="ml-2 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                      {displayProjects.filter(projectRequiresAction).length}
                    </span>
                  )}
                </button>
              )}
              <button
                onClick={() => setFilter('active')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm ${
                  filter === 'active'
                    ? 'bg-gradient-to-r from-[#FF3333] to-[#FF5555] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Active
              </button>
              <button
                onClick={() => setFilter('completed')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm ${
                  filter === 'completed'
                    ? 'bg-gradient-to-r from-[#FF3333] to-[#FF5555] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Completed
              </button>
            </div>
          )}
        </div>

        {/* Content based on active tab */}
        {activeTab === 'my-projects' && (
          <>
            {projectsLoading || web3Loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3333]"></div>
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-lg text-[#471b007f] font-dm mb-4">
                  No projects found
                </p>
                {!account && (
                  <p className="text-sm text-[#471b007f] font-dm mb-4">
                    Connect your wallet to create your own projects
                  </p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProjects.map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'create-project' && (
          <div className="text-center py-20">
            <p className="text-lg text-[#471b007f] font-dm mb-4">
              Redirecting to project creation...
            </p>
          </div>
        )}
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default DashboardPage;
